import CloseButton from '@atoms/CloseButton/CloseButton';
import Portal from '@atoms/Portal/Portal';
import IconSizeEnum from '@models/enums/IconSizeEnum';
import bodyScrollLock from '@utils/helpers/bodyScrollLock';
import classNames from 'classnames';
import FocusTrap from 'focus-trap-react';
import { motion } from 'framer-motion';
import { PropsWithChildren, useCallback, useEffect, useRef } from 'react';

interface Props {
  readonly onCloseCallback?: () => void;
  readonly containerZIndexClassName?: string;
  readonly contentClasses?: string;
  readonly crossMarginsClassName?: string;
  readonly crossClasses?: string;
  readonly crossColor?: 'black' | 'white';
  readonly isOpen: boolean;
  readonly modalClasses?: string;
  readonly setIsOpen?: (param: boolean) => void;
  readonly showCloseButton?: boolean;
  readonly transparent?: boolean;
  readonly isCloseButtonFixed?: boolean;
  readonly closeButtonIconSize?: IconSizeEnum | string;
  readonly shouldCloseOnClickOutsideContent?: boolean;
  readonly shouldLockBody?: boolean;
}

const Modal = ({
  onCloseCallback,
  children,
  containerZIndexClassName = 'z-max',
  contentClasses,
  crossColor = 'white',
  crossMarginsClassName = 'mr-8 mt-8',
  crossClasses,
  isOpen,
  modalClasses,
  setIsOpen,
  showCloseButton = true,
  transparent = false,
  isCloseButtonFixed = true,
  closeButtonIconSize = IconSizeEnum.md,
  shouldCloseOnClickOutsideContent = false,
  shouldLockBody = true,
}: PropsWithChildren<Props>) => {
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen && shouldLockBody) {
      bodyScrollLock(true);
    }

    return () => {
      if (shouldLockBody) bodyScrollLock(false);
    };
  }, [isOpen, shouldLockBody]);

  const close = useCallback(() => {
    setIsOpen?.(false);
    onCloseCallback?.();
  }, [onCloseCallback, setIsOpen]);

  useEffect(() => {
    const keyboardClose = (ev: KeyboardEvent) => {
      if (ev.key === 'Escape' && isOpen === true) {
        setIsOpen?.(false);
      }
    };
    window.addEventListener('keydown', keyboardClose);

    return () => {
      window.removeEventListener('keydown', keyboardClose);
    };
  }, [isOpen, setIsOpen]);

  useEffect(() => {
    const handleClickOutsideContent = (ev: Event) => {
      if (
        isOpen &&
        shouldCloseOnClickOutsideContent &&
        !contentRef.current?.contains(ev.target as HTMLElement)
      ) {
        close();
      }
    };

    window.addEventListener('click', handleClickOutsideContent, true);

    return () => {
      window.removeEventListener('click', handleClickOutsideContent, true);
    };
  }, [close, isOpen, shouldCloseOnClickOutsideContent]);

  if (!isOpen) {
    return null;
  }

  return (
    <Portal key="portal" mountId="drawer-root">
      <FocusTrap
        focusTrapOptions={{
          preventScroll: true,
          escapeDeactivates: false,
        }}
      >
        <motion.div
          animate={{ opacity: 1 }}
          className={classNames('relative h-auto', containerZIndexClassName)}
          exit={{ opacity: 0 }}
          initial={{ opacity: 0 }}
          transition={{
            duration: 0.8,
            ease: [0.04, 0.62, 0.23, 0.98],
          }}
        >
          <section
            className={classNames(
              transparent ? 'bg-black-100/50' : 'bg-black-100',
              'fixed inset-0 z-100 text-white'
            )}
          >
            <div className="flex h-full w-full">
              <div
                className={classNames(
                  modalClasses,
                  'container h-full w-full overflow-y-auto'
                )}
              >
                <div
                  className={classNames(contentClasses, 'relative text-left')}
                  ref={contentRef}
                >
                  {showCloseButton && (
                    <>
                      {/* A hidden button that steals the inital focus of the modal preventing the close icon from animating on modal open  */}
                      <button className="hidden" aria-hidden="true" />

                      <CloseButton
                        color={crossColor}
                        size={closeButtonIconSize}
                        onClick={close}
                        className={classNames(
                          'top-0 right-0 z-100 flex cursor-pointer flex-col items-center',
                          isCloseButtonFixed ? 'fixed' : 'absolute',
                          crossMarginsClassName,
                          crossClasses
                        )}
                      />
                    </>
                  )}
                  {children}
                </div>
              </div>
            </div>
          </section>
        </motion.div>
      </FocusTrap>
    </Portal>
  );
};

export default Modal;
